import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { rpc } from '../../api/rpc'
import { Button } from '../ui/Button'
import { Field, Input } from '../ui/Field'
import { Select } from '../ui/Select'
import { useToast } from '../ui/Toast'
import { confirmDialog } from '../ui/Confirm'
import { useWorkspaces } from '../useWorkspaces'
import { useRepos, type RepoRef } from './useRepos'

interface RemoteRepo { repo: string; private: boolean; defaultBranch: string; canPush: boolean }

/**
 * Repository administration inside a connected GitHub card (docs/connectors.md §10): register a
 * repository in a workspace, change its read-only flag, unregister it, or delete the local clone.
 *
 * The workspace is a choice, not a constant: a repository belongs to a workspace, so a team
 * workspace is where a shared clone lives. Registering only into the personal workspace would make
 * the whole team path unreachable from the UI.
 *
 * Unregistering and deleting the clone are deliberately separate actions: unregistering only drops
 * the row and always keeps the code, while deleting the clone is blocked while there is work that
 * was never pushed. Nothing here ever touches GitHub.
 */
export function RepoAdmin({ connectorId, onChanged }: { connectorId: string; onChanged?(): void }) {
  const { t } = useTranslation()
  const toast = useToast()
  const { items: workspaces } = useWorkspaces()
  const writable = workspaces.filter(w => w.role !== 'viewer')
  const [workspaceId, setWorkspaceId] = useState('')
  const { items, reload } = useRepos(workspaceId || undefined)

  const [remote, setRemote] = useState<RemoteRepo[] | null>(null)
  const [picked, setPicked] = useState('')
  const [manual, setManual] = useState(false)
  const [owner, setOwner] = useState('')
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)

  // Default to the personal workspace, which every user has.
  useEffect(() => {
    if (!workspaceId && writable.length) setWorkspaceId(writable.find(w => w.kind === 'personal')?.id ?? writable[0].id)
  }, [workspaceId, writable])

  // The list of repositories the credential can reach, so the user picks instead of typing.
  useEffect(() => {
    setRemote(null)
    void rpc.call<{ items: RemoteRepo[] }>('connectors.github.repos', { connectorId })
      .then(r => setRemote(r.items)).catch(() => setRemote([]))
  }, [connectorId])

  const refresh = () => { void reload(); onChanged?.() }

  const add = async (e: React.FormEvent) => {
    e.preventDefault()
    const [o, n] = manual ? [owner.trim(), name.trim()] : (picked.split('/') as [string, string])
    if (!o || !n) return
    setBusy(true)
    try {
      const known = remote?.find(r => r.repo === `${o}/${n}`)
      await rpc.call('repos.add', {
        workspaceId, connectorId, owner: o, name: n,
        ...(known ? { defaultBranch: known.defaultBranch } : {})
      })
      setPicked(''); setOwner(''); setName('')
      refresh()
      toast(t('repos.added'), 'success')
    } catch (err) { toast((err as Error).message, 'error') } finally { setBusy(false) }
  }

  const setReadOnly = async (repo: RepoRef, readOnly: boolean) => {
    try { await rpc.call('repos.update', { repoId: repo.id, readOnly }); refresh() }
    catch (err) { toast((err as Error).message, 'error') }
  }

  const remove = async (repo: RepoRef) => {
    if (!(await confirmDialog(`${repo.owner}/${repo.name} — ${t('repos.removeHint')}`, { confirmLabel: t('repos.remove') }))) return
    try { await rpc.call('repos.remove', { repoId: repo.id }); refresh() }
    catch (err) { toast((err as Error).message, 'error') }
  }

  const deleteClone = async (repo: RepoRef, force = false) => {
    try {
      const r = await rpc.call<{ deleted: boolean; dirty?: string[]; unpushed?: number }>('repos.deleteClone', { repoId: repo.id, force })
      if (!r.deleted) {
        // Work that was never pushed: ask before destroying it, and name what is at risk.
        const ok = await confirmDialog(t('repos.deleteBlocked', { dirty: r.dirty?.length ?? 0, unpushed: r.unpushed ?? 0 }), { confirmLabel: t('repos.deleteClone') })
        if (ok) await deleteClone(repo, true)
        return
      }
      refresh()
      toast(t('repos.cloneDeleted'), 'success')
    } catch (err) { toast((err as Error).message, 'error') }
  }

  const already = new Set(items.map(r => `${r.owner}/${r.name}`))
  const choices = (remote ?? []).filter(r => !already.has(r.repo))

  return (
    <section className="space-y-3">
      <p className="text-sm font-medium">{t('connectors.github.repos')}</p>

      {writable.length > 1 && (
        <div className="max-w-[18rem]">
          <Select
            label={t('teams.workspace')} value={workspaceId} onChange={setWorkspaceId}
            options={writable.map(w => ({
              value: w.id,
              label: `${w.kind === 'personal' ? t('teams.personal') : `👥 ${w.name}`}`
            }))}
          />
        </div>
      )}

      {items.length > 0 && (
        <ul className="divide-y divide-line rounded-lg border border-line">
          {items.map(repo => (
            <li key={repo.id} className="flex flex-wrap items-center gap-2 px-3 py-2 text-sm">
              <span className="font-mono text-[13px]">{repo.owner}/{repo.name}</span>
              <span className="text-xs text-ink-500">{repo.defaultBranch}</span>
              {repo.readOnly && <span className="rounded bg-cream-200 px-1.5 py-0.5 text-[11px] text-ink-400">{t('repos.readOnly')}</span>}
              <span className="ml-auto flex items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => void setReadOnly(repo, !repo.readOnly)}>
                  {repo.readOnly ? t('repos.allowPush') : t('repos.makeReadOnly')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => void remove(repo)}>{t('repos.remove')}</Button>
                <Button size="sm" variant="ghost" onClick={() => void deleteClone(repo)}>{t('repos.deleteClone')}</Button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <form className="flex flex-wrap items-end gap-2" onSubmit={add}>
        {!manual && choices.length > 0 ? (
          <div className="min-w-[16rem]">
            <Select
              label={t('repos.pickRemote')} value={picked} onChange={setPicked}
              placeholder={t('repos.pickRemotePlaceholder')}
              options={choices.map(r => ({
                value: r.repo,
                label: `${r.repo}${r.private ? ` · ${t('repos.private')}` : ''}`,
                hint: r.defaultBranch
              }))}
            />
          </div>
        ) : (
          <>
            <Field label={t('repos.owner')}>{id => <Input id={id} required value={owner} onChange={e => setOwner(e.target.value)} placeholder="octocat" />}</Field>
            <Field label={t('repos.name')}>{id => <Input id={id} required value={name} onChange={e => setName(e.target.value)} placeholder="hello-world" />}</Field>
          </>
        )}
        <Button type="submit" loading={busy} disabled={!workspaceId}>{t('repos.add')}</Button>
        {choices.length > 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setManual(m => !m)}>
            {manual ? t('repos.pickFromList') : t('repos.enterManually')}
          </Button>
        )}
      </form>
    </section>
  )
}
